import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { TrendingUp, Receipt, IndianRupee, Wallet } from "lucide-react";

const COLORS = ["#10B981", "#F59E0B", "#06B6D4", "#A855F7", "#EF4444"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ date_from: "", date_to: "" });

  const load = () => {
    const params = {};
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    api.get("/dashboard", { params }).then((r) => setData(r.data));
  };
  useEffect(() => {
    load();
  }, [range]);

  if (!data) return <div className="p-6 text-[#6B7280]">Loading…</div>;

  const cards = [
    { label: "Today's Sales", value: inr(data.today_sales), icon: IndianRupee, color: "#10B981" },
    { label: "Today's Bills", value: data.today_bills, icon: Receipt, color: "#06B6D4" },
    { label: "Total Sales", value: inr(data.total_sales), icon: TrendingUp, color: "#F59E0B" },
    { label: "Avg. Bill", value: inr(data.avg_bill), icon: Wallet, color: "#A855F7" },
  ];

  return (
    <div className="p-6 pb-16">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold">Sales Dashboard</h1>
          <p className="text-sm text-[#6B7280]">{data.total_bills} paid bills in range</p>
        </div>
        <div className="flex gap-2 items-center text-sm">
          <input type="date" className="bg-[#0B0F17] border border-[#23334D] rounded-lg px-3 py-2 text-white outline-none" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} />
          <span className="text-[#6B7280]">to</span>
          <input type="date" className="bg-[#0B0F17] border border-[#23334D] rounded-lg px-3 py-2 text-white outline-none" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#6B7280] uppercase tracking-wide font-semibold">{c.label}</span>
              <c.icon className="w-5 h-5" style={{ color: c.color }} />
            </div>
            <div className="font-head font-bold text-2xl mt-2 font-mono">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Sales Trend</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#23334D" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#162032", border: "1px solid #23334D", borderRadius: 8, color: "#fff" }} formatter={(v) => inr(v)} />
              <Bar dataKey="amount" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Payment Split</h2>
          {data.payment_split.length === 0 ? (
            <div className="text-[#6B7280] text-sm py-10 text-center">No data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.payment_split} dataKey="amount" nameKey="method" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {data.payment_split.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#162032", border: "1px solid #23334D", borderRadius: 8, color: "#fff" }} formatter={(v) => inr(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {data.payment_split.map((p, i) => (
                  <div key={p.method} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.method}</span>
                    <span className="font-mono">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Top Selling Items</h2>
          <div className="space-y-2">
            {data.top_items.map((it, i) => (
              <div key={it.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-3"><span className="text-[#6B7280] w-5 font-mono">{i + 1}</span>{it.name}</span>
                <span className="text-[#9CA3AF]"><b className="text-white font-mono">{it.qty}</b> sold · <span className="font-mono">{inr(it.amount)}</span></span>
              </div>
            ))}
            {data.top_items.length === 0 && <div className="text-[#6B7280] text-sm">No data</div>}
          </div>
        </div>

        <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Cashier Performance</h2>
          <div className="space-y-2">
            {data.cashier_sales.map((c) => (
              <div key={c.name} className="flex items-center justify-between text-sm">
                <span>{c.name}</span>
                <span className="text-[#9CA3AF]"><b className="text-white font-mono">{c.bills}</b> bills · <span className="font-mono">{inr(c.amount)}</span></span>
              </div>
            ))}
            {data.cashier_sales.length === 0 && <div className="text-[#6B7280] text-sm">No data</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
